import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Search,
  Upload,
  FileText,
  FileSpreadsheet,
  Presentation,
  File,
  Trash2,
  Database,
  Check,
  Share2,
  Users,
  Globe,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Circle,
  CheckCircle2,
  Link2,
  Eye,
  Table,
  Sheet,
  FileJson,
  FolderOpen,
  Plus,
  Pencil,
  ChevronLeft,
  BookOpen,
} from 'lucide-react';
import { API_ENDPOINTS } from '../config';
import { useAuth } from '../contexts/AuthContext';
import FilePreviewModal from './FilePreviewModal';

const API = API_ENDPOINTS.main;

// File type icons and categories (matches Taurus FilePanel)
const FILE_ICONS = {
  '.csv': Table,
  '.xlsx': Sheet,
  '.xls': Sheet,
  '.json': FileJson,
  '.pdf': File,
  '.docx': FileText,
  '.doc': FileText,
  '.pptx': Presentation,
  '.ppt': Presentation,
  '.txt': FileText,
  '.md': FileText,
};

const CATEGORY_LABELS = {
  tabular: 'Spreadsheets & Data',
  document: 'Documents',
};

const CATEGORY_ORDER = ['tabular', 'document'];

// Database type formatting
const formatDbType = (dbType) => {
  if (!dbType) return '';
  const types = {
    'postgresql': 'PostgreSQL',
    'postgres': 'PostgreSQL',
    'mysql': 'MySQL',
    'mssql': 'SQL Server',
    'sqlserver': 'SQL Server',
    'snowflake': 'Snowflake',
  };
  return types[dbType.toLowerCase()] || dbType;
};

export default function DataSourcesPanel({
  isOpen,
  onClose,
  // Database props
  datasources,
  activeDatasource,
  onActivateDatasource,
  // File props
  selectedFiles,
  onSelectFiles,
  includeDatabase,
  onIncludeDatabaseChange,
  // Notification
  showNotification,
  // Projects
  activeProject,
  onActivateProject,
  onDeactivateProject,
}) {
  const { user, tierLimits } = useAuth();
  const [files, setFiles] = useState([]);
  const [usage, setUsage] = useState({ file_count: 0, total_size_mb: 0 });
  const [limits, setLimits] = useState({ max_files: 5, max_file_size_mb: 1, max_total_storage_mb: 5 });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activatingDb, setActivatingDb] = useState(null); // Track which DB is being activated
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ databases: true, files: true });
  const [expandedCategories, setExpandedCategories] = useState({ tabular: true, document: true });
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [importingUrl, setImportingUrl] = useState(false);
  const [previewingFile, setPreviewingFile] = useState(null); // Track which file is being previewed
  const [editMode, setEditMode] = useState(false); // Edit mode for bulk file operations
  const [editSelectedIds, setEditSelectedIds] = useState(new Set()); // File IDs selected for deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Confirm modal for bulk delete
  const [bulkDeleting, setBulkDeleting] = useState(false); // Loading state for bulk delete
  const fileInputRef = useRef(null);
  const urlInputRef = useRef(null);
  const lastFetchRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const lastProjectFetchRef = useRef(null);

  // Projects tab state
  const [activeTab, setActiveTab] = useState('sources');
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [editingProject, setEditingProject] = useState(null); // null = list view, object = edit view
  const [projectForm, setProjectForm] = useState({ name: '', description: '', context_text: '', datasource_name: '', file_ids: [] });
  const [savingProject, setSavingProject] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [projectsError, setProjectsError] = useState(null);

  // Cache duration: 30 minutes (cache auto-refreshes on datasource edits)
  const CACHE_DURATION_MS = 30 * 60 * 1000;

  // Fetch files with smart caching
  const fetchFiles = useCallback(async (forceRefresh = false) => {
    if (!isOpen) return;

    const now = Date.now();
    const cacheValid = lastFetchRef.current && (now - lastFetchRef.current < CACHE_DURATION_MS);

    // Skip fetch if cache is valid and not forcing refresh
    if (cacheValid && hasFetchedRef.current && !forceRefresh) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/files/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }

      const data = await response.json();
      setFiles(data.files || []);
      setUsage(data.usage || { file_count: 0, total_size_mb: 0 });
      setLimits(data.limits || { max_files: 5, max_file_size_mb: 1, max_total_storage_mb: 5 });
      lastFetchRef.current = now;
      hasFetchedRef.current = true;
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      fetchFiles();
    }
  }, [isOpen, fetchFiles]);

  // Fetch projects when Projects tab becomes active (30-min cache, same pattern as fetchFiles)
  const fetchProjects = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && lastProjectFetchRef.current && (now - lastProjectFetchRef.current < CACHE_DURATION_MS)) {
      return;
    }
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/virgo-projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load projects');
      setProjects(await res.json());
      lastProjectFetchRef.current = now;
    } catch (err) {
      setProjectsError('Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && activeTab === 'projects') {
      fetchProjects();
    }
  }, [isOpen, activeTab, fetchProjects]);

  // Cancel / back from edit form
  const handleCancelEdit = () => {
    setEditingProject(null);
    setProjectsError(null);
  };

  // Open create form
  const handleNewProject = () => {
    setProjectForm({ name: '', description: '', context_text: '', datasource_name: '', file_ids: [] });
    setEditingProject({ _isNew: true });
    setProjectsError(null);
  };

  // Open edit form
  const handleEditProject = (project) => {
    setProjectForm({
      name: project.name,
      description: project.description || '',
      context_text: project.context_text || '',
      datasource_name: project.datasource_name || '',
      file_ids: project.file_ids || [],
    });
    setEditingProject(project);
    setProjectsError(null);
  };

  // Save (create or update)
  const handleSaveProject = async () => {
    if (!projectForm.name.trim()) {
      setProjectsError('Project name is required');
      return;
    }
    setSavingProject(true);
    setProjectsError(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: projectForm.name.trim(),
        description: projectForm.description.trim(),
        context_text: projectForm.context_text.trim(),
        datasource_name: projectForm.datasource_name || null,
        file_ids: projectForm.file_ids,
      };
      const isNew = editingProject?._isNew;
      const url = isNew ? `${API}/virgo-projects` : `${API}/virgo-projects/${editingProject.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      lastProjectFetchRef.current = null; // Invalidate cache
      await fetchProjects();
      setEditingProject(null);
      if (showNotification) showNotification('success', isNew ? 'Project created' : 'Project saved');
    } catch (err) {
      setProjectsError(err.message || 'Save failed');
    } finally {
      setSavingProject(false);
    }
  };

  // Delete a project
  const handleDeleteProject = async (projectId) => {
    setDeletingProjectId(projectId);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/virgo-projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      // If this was the active project, deactivate
      if (activeProject?.id === projectId && onDeactivateProject) {
        onDeactivateProject();
      }
      lastProjectFetchRef.current = null; // Invalidate cache
      await fetchProjects();
    } catch (err) {
      setProjectsError('Failed to delete project');
    } finally {
      setDeletingProjectId(null);
    }
  };

  // Activate a project from the list
  const handleActivateFromList = (project) => {
    if (!onActivateProject) return;
    // Resolve file objects from the currently loaded files list — Set for O(1) lookup
    const fileIdSet = new Set(project.file_ids || []);
    const resolvedFiles = files.filter(f => fileIdSet.has(f.id));
    onActivateProject(project, resolvedFiles);
    onClose();
  };

  // Toggle a file in the project form's file_ids list
  const toggleProjectFile = (fileId) => {
    setProjectForm(prev => {
      const ids = prev.file_ids || [];
      const next = ids.includes(fileId) ? ids.filter(id => id !== fileId) : [...ids, fileId];
      return { ...prev, file_ids: next };
    });
  };

  // Handle database activation
  const handleActivateDatabase = async (datasource) => {
    if (activatingDb) return; // Prevent double-clicks

    setActivatingDb(datasource.name);
    setError(null);

    try {
      // Pass false to prevent closing the DatasourceSelectionModal (we're in the panel)
      await onActivateDatasource(datasource, false);
      // Auto-enable includeDatabase when user activates a datasource
      if (!includeDatabase) {
        onIncludeDatabaseChange(true);
      }
    } catch (err) {
      console.error('Error activating datasource:', err);
      setError('Failed to activate datasource');
    } finally {
      setActivatingDb(null);
    }
  };

  // Handle file upload
  const handleUpload = async (event) => {
    const uploadedFiles = event.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    // Client-side validation BEFORE upload
    for (const file of uploadedFiles) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > limits.max_file_size_mb) {
        setError(`File "${file.name}" is too large (${fileSizeMB.toFixed(1)} MB). Maximum size for your plan: ${limits.max_file_size_mb} MB.`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }

    setUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      for (const file of uploadedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API}/files/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Upload failed');
        }
      }

      // Refresh file list (force refresh after upload)
      await fetchFiles(true);
      if (showNotification) {
        showNotification('success', 'File uploaded successfully');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle URL import (Google Drive)
  const handleUrlImport = async () => {
    if (!urlValue.trim()) return;

    setImportingUrl(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/files/from-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: urlValue.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import failed');
      }

      // Refresh file list (force refresh after import)
      await fetchFiles(true);
      setUrlValue('');
      setShowUrlInput(false);
      if (showNotification) {
        showNotification('success', 'File imported from Google Drive');
      }
    } catch (err) {
      console.error('URL import error:', err);
      setError(err.message || 'Failed to import from URL');
    } finally {
      setImportingUrl(false);
    }
  };

  // Handle file sharing (owner: demo, admin: org)
  const handleShare = async (fileId, currentShareLevel, event) => {
    event.stopPropagation();

    // Determine what action to take
    const isCurrentlyShared = currentShareLevel !== null;
    const shareLevel = user?.role === 'owner' ? 'demo' : 'org';

    try {
      const token = localStorage.getItem('token');

      if (isCurrentlyShared) {
        // Unshare
        const response = await fetch(`${API}/files/unshare/${fileId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to unshare');
        if (showNotification) showNotification('success', 'File is now private');
      } else {
        // Share
        const response = await fetch(`${API}/files/share`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ file_id: fileId, share_level: shareLevel })
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Failed to share');
        }
        if (showNotification) {
          showNotification('success', shareLevel === 'demo' ? 'File shared with all users' : 'File shared with organization');
        }
      }

      await fetchFiles(true);
    } catch (err) {
      console.error('Share error:', err);
      setError(err.message || 'Failed to update sharing');
    }
  };

  // Handle file deletion
  const handleDelete = async (fileId, event) => {
    event.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      // Remove from selected if it was selected
      onSelectFiles(selectedFiles.filter(f => f.id !== fileId));

      // Refresh list (force refresh after delete)
      await fetchFiles(true);
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete file');
    }
  };

  // Handle file preview
  const handlePreview = (file, event) => {
    event.stopPropagation(); // Prevent file selection toggle
    setPreviewingFile(file);
  };

  // Edit mode: Toggle file selection for deletion
  const toggleEditSelection = (fileId, event) => {
    event.stopPropagation();
    setEditSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Edit mode: Select/deselect all own files
  const toggleSelectAll = () => {
    const ownFiles = files.filter(f => f.is_own);
    if (editSelectedIds.size === ownFiles.length) {
      setEditSelectedIds(new Set());
    } else {
      setEditSelectedIds(new Set(ownFiles.map(f => f.id)));
    }
  };

  // Edit mode: Cancel and reset
  const cancelEditMode = () => {
    setEditMode(false);
    setEditSelectedIds(new Set());
  };

  // Edit mode: Bulk delete handler
  const handleBulkDelete = async () => {
    if (editSelectedIds.size === 0) return;

    setBulkDeleting(true);
    const token = localStorage.getItem('token');
    let successCount = 0;
    let failCount = 0;

    for (const fileId of editSelectedIds) {
      try {
        const response = await fetch(`${API}/files/${fileId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          successCount++;
          // Remove from query context selection if it was selected
          onSelectFiles(selectedFiles.filter(f => f.id !== fileId));
        } else {
          failCount++;
        }
      } catch (err) {
        console.error('Delete error:', err);
        failCount++;
      }
    }

    // Refresh file list
    await fetchFiles(true);

    // Show notification
    if (showNotification) {
      if (failCount === 0) {
        showNotification('success', `Deleted ${successCount} file${successCount !== 1 ? 's' : ''}`);
      } else {
        showNotification('error', `Deleted ${successCount}, failed ${failCount}`);
      }
    }

    // Reset edit mode
    setBulkDeleting(false);
    setShowDeleteConfirm(false);
    setEditMode(false);
    setEditSelectedIds(new Set());
  };

  // Check if file can be previewed (tabular files only)
  const isPreviewable = (fileType) => {
    return ['.csv', '.xlsx', '.xls', '.json'].includes(fileType);
  };

  // Toggle file selection
  const toggleFileSelection = (file) => {
    const isSelected = selectedFiles.some(f => f.id === file.id);

    if (isSelected) {
      onSelectFiles(selectedFiles.filter(f => f.id !== file.id));
    } else {
      onSelectFiles([...selectedFiles, file]);
    }
  };

  // Filter files by search
  const filteredFiles = files.filter(file =>
    file.original_filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter datasources by search
  const filteredDatasources = (datasources || []).filter(ds =>
    ds.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group files by category
  const groupedFiles = CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = filteredFiles.filter(f => f.category === category);
    return acc;
  }, {});

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get icon for file type
  const getFileIcon = (fileType) => {
    return FILE_ICONS[fileType] || File;
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Calculate context summary
  const dbIncluded = includeDatabase && activeDatasource;
  const fileCount = selectedFiles.length;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/50 z-30"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-96 bg-white dark:bg-gray-900 shadow-2xl z-40 flex flex-col border-l border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-purple-700">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <Database className="w-4 h-4" />
            Data Sources
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1 rounded hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Bar */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('sources')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
              activeTab === 'sources'
                ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <Database className="w-3.5 h-3.5" />
            All Sources
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
              activeTab === 'projects'
                ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <FolderOpen className="w-3.5 h-3.5" />
            Projects
            {activeProject && (
              <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
            )}
          </button>
        </div>

        {/* ── PROJECTS TAB ── */}
        {activeTab === 'projects' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {editingProject ? (
              /* ── Project Edit / Create Form ── */
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Form Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {editingProject._isNew ? 'New Project' : 'Edit Project'}
                  </h3>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                  {projectsError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-700 dark:text-red-400">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {projectsError}
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={projectForm.name}
                      onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. Q2 Marketing Analysis"
                      maxLength={100}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={projectForm.description}
                      onChange={e => setProjectForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Short description (optional)"
                      maxLength={500}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Context / Instructions */}
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <BookOpen className="w-3 h-3" />
                      Instructions
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                      Always included in every query for this project.
                    </p>
                    <textarea
                      value={projectForm.context_text}
                      onChange={e => setProjectForm(p => ({ ...p, context_text: e.target.value }))}
                      placeholder={'e.g. "Focus only on Q2 2025 data. The \'date\' column is always UTC. Prefer bar charts."'}
                      maxLength={10000}
                      rows={5}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  {/* Database */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Database
                    </label>
                    <select
                      value={projectForm.datasource_name || ''}
                      onChange={e => setProjectForm(p => ({ ...p, datasource_name: e.target.value || null }))}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">None</option>
                      {(datasources || []).map(ds => (
                        <option key={ds.name} value={ds.name}>{ds.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Files */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Files ({projectForm.file_ids.length} selected)
                    </label>
                    {loading ? (
                      <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading files…
                      </div>
                    ) : files.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 py-2">No files uploaded yet. Upload files in the All Sources tab.</p>
                    ) : (
                      <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                        {files.map(f => {
                          const FileIcon = getFileIcon(f.file_type);
                          const checked = projectForm.file_ids.includes(f.id);
                          return (
                            <label key={f.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 dark:hover:bg-gray-800 rounded">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleProjectFile(f.id)}
                                className="w-3.5 h-3.5 rounded accent-purple-600"
                              />
                              <FileIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{f.original_filename || f.filename}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Save / Cancel */}
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                  <button
                    onClick={handleCancelEdit}
                    className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProject}
                    disabled={savingProject || !projectForm.name.trim()}
                    className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {savingProject ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {savingProject ? 'Saving…' : 'Save Project'}
                  </button>
                </div>
              </div>
            ) : (
              /* ── Project List View ── */
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* List Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {projects.length} project{projects.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={handleNewProject}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    New Project
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {projectsLoading ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading…</span>
                    </div>
                  ) : projectsError ? (
                    <div className="px-4 py-8 text-center text-sm text-red-500">{projectsError}</div>
                  ) : projects.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <FolderOpen className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">No projects yet</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">Projects keep files, a database, and instructions together.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {projects.map(project => {
                        const isActive = activeProject?.id === project.id;
                        const isDeleting = deletingProjectId === project.id;
                        return (
                          <div
                            key={project.id}
                            className={`group px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${isActive ? 'bg-purple-50 dark:bg-purple-900/20' : ''}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <button
                                onClick={() => handleActivateFromList(project)}
                                className="flex-1 text-left min-w-0"
                              >
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0" />}
                                  <span className={`text-sm font-medium truncate ${isActive ? 'text-purple-700 dark:text-purple-300' : 'text-gray-800 dark:text-gray-200'}`}>
                                    {project.name}
                                  </span>
                                </div>
                                {project.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1.5">{project.description}</p>
                                )}
                                <div className="flex flex-wrap gap-1.5">
                                  {project.datasource_name && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">
                                      <Database className="w-2.5 h-2.5" />
                                      {project.datasource_name}
                                    </span>
                                  )}
                                  {project.file_ids?.length > 0 && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                                      <FileText className="w-2.5 h-2.5" />
                                      {project.file_ids.length} file{project.file_ids.length !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                  {project.context_text && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                                      <BookOpen className="w-2.5 h-2.5" />
                                      Instructions
                                    </span>
                                  )}
                                </div>
                              </button>

                              {/* Actions */}
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <button
                                  onClick={() => handleEditProject(project)}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded transition-colors"
                                  title="Edit project"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteProject(project.id)}
                                  disabled={isDeleting}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors disabled:opacity-50"
                                  title="Delete project"
                                >
                                  {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </div>

                            {/* Active badge + deactivate */}
                            {isActive && (
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-purple-100 dark:border-purple-800/50">
                                <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">Active project</span>
                                <button
                                  onClick={onDeactivateProject}
                                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                >
                                  Exit project
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ALL SOURCES TAB content (existing) ── */}
        {activeTab === 'sources' && (
          <>

        {/* Search and Upload */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search databases & files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {/* Upload and URL Import Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || importingUrl || (usage.file_count || 0) >= (limits.max_files || 5)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload
                </>
              )}
            </button>
            <button
              onClick={() => {
                setShowUrlInput(!showUrlInput);
                setTimeout(() => urlInputRef.current?.focus(), 100);
              }}
              disabled={uploading || importingUrl || (usage.file_count || 0) >= (limits.max_files || 5)}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showUrlInput
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              } disabled:bg-gray-400 disabled:text-white`}
              title="Import from Google Drive URL"
            >
              <Link2 className="w-4 h-4" />
              URL
            </button>
          </div>
          {/* URL Input Field (collapsible) */}
          {showUrlInput && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  ref={urlInputRef}
                  type="text"
                  placeholder="Paste Google Drive link..."
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && urlValue.trim()) {
                      handleUrlImport();
                    } else if (e.key === 'Escape') {
                      setShowUrlInput(false);
                      setUrlValue('');
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleUrlImport}
                  disabled={!urlValue.trim() || importingUrl}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {importingUrl ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Import'
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                💡 Make sure file is shared: Share → "Anyone with the link"
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleUpload}
            multiple
            accept=".csv,.xlsx,.xls,.json,.pdf,.docx,.doc,.pptx,.ppt,.txt,.md"
            className="hidden"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm flex-1">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Databases Section */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection('databases')}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center gap-2">
                {expandedSections.databases ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                <Database className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Databases
                </span>
                <span className="text-xs text-gray-500">
                  ({filteredDatasources.length})
                </span>
              </div>
              {dbIncluded && (
                <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                  Active
                </span>
              )}
            </button>

            {expandedSections.databases && (
              <div className="px-4 pb-3">
                {/* Include Database Toggle */}
                <label className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDatabase}
                    onChange={(e) => onIncludeDatabaseChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    disabled={!activeDatasource}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Include database in queries
                  </span>
                </label>

                {/* Database List */}
                {filteredDatasources.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No databases found
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Add one in Settings
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredDatasources.map((ds) => {
                      const isActive = activeDatasource?.name === ds.name;
                      const isActivating = activatingDb === ds.name;

                      return (
                        <button
                          key={ds.id || ds.name}
                          onClick={() => !isActive && handleActivateDatabase(ds)}
                          disabled={isActive || isActivating}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left ${
                            isActive
                              ? includeDatabase
                                ? 'bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500'
                                : 'bg-gray-100 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-800 border-2 border-transparent'
                          }`}
                        >
                          {/* Status Icon */}
                          <div className="flex-shrink-0">
                            {isActivating ? (
                              <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                            ) : isActive ? (
                              <CheckCircle2 className={`w-4 h-4 ${includeDatabase ? 'text-purple-600' : 'text-gray-400'}`} />
                            ) : (
                              <Circle className="w-4 h-4 text-gray-400" />
                            )}
                          </div>

                          {/* Database Info */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {ds.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDbType(ds.db_type)}
                              {ds.read_only === false ? (
                                <span className="text-amber-600 dark:text-amber-400"> · Full access</span>
                              ) : (
                                <span> · Read-only</span>
                              )}
                            </div>
                          </div>

                          {/* Active Badge */}
                          {isActive && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              includeDatabase
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-400 text-white'
                            }`}>
                              {includeDatabase ? 'Active' : 'Excluded'}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Files Section */}
          <div>
            <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <button
                onClick={() => toggleSection('files')}
                className="flex items-center gap-2 flex-1"
              >
                {expandedSections.files ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Files
                </span>
                <span className="text-xs text-gray-500">
                  ({usage.file_count || 0}/{limits.max_files || 5})
                </span>
              </button>
              <div className="flex items-center gap-2">
                {fileCount > 0 && !editMode && (
                  <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                    {fileCount} selected
                  </span>
                )}
                {/* Edit/Done button - only show if user has own files */}
                {files.some(f => f.is_own) && expandedSections.files && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editMode) {
                        cancelEditMode();
                      } else {
                        setEditMode(true);
                      }
                    }}
                    className={`text-xs font-medium px-2 py-1 rounded transition-colors ${
                      editMode
                        ? 'text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {editMode ? 'Done' : 'Edit'}
                  </button>
                )}
              </div>
            </div>

            {expandedSections.files && (
              <div className="px-4 pb-3">
                {/* Edit Mode Action Bar */}
                {editMode && files.some(f => f.is_own) && (
                  <div className="flex items-center justify-between mb-3 px-2 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editSelectedIds.size === files.filter(f => f.is_own).length && files.filter(f => f.is_own).length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Select All
                      </span>
                    </label>
                    {editSelectedIds.size > 0 && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete ({editSelectedIds.size})
                      </button>
                    )}
                  </div>
                )}

                {/* File List */}
                {loading ? (
                  <div className="flex items-center justify-center h-20">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : files.length === 0 ? (
                  <div className="text-center py-4">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No files uploaded yet
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Upload CSV, Excel, PDF, or other files
                    </p>
                  </div>
                ) : (
                  <div>
                    {CATEGORY_ORDER.map(category => {
                      const categoryFiles = groupedFiles[category];
                      if (!categoryFiles || categoryFiles.length === 0) return null;

                      return (
                        <div key={category} className="mb-2">
                          {/* Category Header */}
                          <button
                            onClick={() => toggleCategory(category)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                          >
                            {expandedCategories[category] ? (
                              <ChevronDown className="w-3 h-3" />
                            ) : (
                              <ChevronRight className="w-3 h-3" />
                            )}
                            {CATEGORY_LABELS[category]}
                            <span className="text-gray-400 dark:text-gray-500 font-normal">
                              ({categoryFiles.length})
                            </span>
                          </button>

                          {/* Category Files */}
                          {expandedCategories[category] && (
                            <div className="mt-1 space-y-1">
                              {categoryFiles.map(file => {
                                const FileIcon = getFileIcon(file.file_type);
                                const isSelected = selectedFiles.some(f => f.id === file.id);
                                const isEditSelected = editSelectedIds.has(file.id);

                                return (
                                  <div
                                    key={file.id}
                                    onClick={() => {
                                      if (editMode && file.is_own) {
                                        toggleEditSelection(file.id, { stopPropagation: () => {} });
                                      } else if (!editMode) {
                                        toggleFileSelection(file);
                                      }
                                    }}
                                    className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                                      editMode
                                        ? isEditSelected
                                          ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-400'
                                          : 'hover:bg-gray-100 dark:hover:bg-gray-800 border-2 border-transparent'
                                        : isSelected
                                          ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                                          : 'hover:bg-gray-100 dark:hover:bg-gray-800 border-2 border-transparent'
                                    }`}
                                  >
                                    {/* Selection/Edit Checkbox */}
                                    {editMode ? (
                                      // Edit mode: show delete checkbox only for own files
                                      file.is_own ? (
                                        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                          isEditSelected
                                            ? 'bg-red-600 text-white'
                                            : 'border-2 border-gray-300 dark:border-gray-600'
                                        }`}>
                                          {isEditSelected && <Check className="w-3 h-3" />}
                                        </div>
                                      ) : (
                                        <div className="w-5 h-5 flex-shrink-0" /> // Spacer for shared files
                                      )
                                    ) : (
                                      // Normal mode: show query selection checkbox
                                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                        isSelected
                                          ? 'bg-green-600 text-white'
                                          : 'border-2 border-gray-300 dark:border-gray-600'
                                      }`}>
                                        {isSelected && <Check className="w-3 h-3" />}
                                      </div>
                                    )}

                                    {/* File Icon */}
                                    <FileIcon className={`w-4 h-4 flex-shrink-0 ${
                                      isSelected
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-gray-500 dark:text-gray-400'
                                    }`} />

                                    {/* File Info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                          {file.original_filename}
                                        </span>
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {formatSize(file.file_size)}
                                      </div>
                                    </div>

                                    {/* Preview Button (eye icon for tabular files) */}
                                    {isPreviewable(file.file_type) && (
                                      <button
                                        onClick={(e) => handlePreview(file, e)}
                                        className="p-1.5 text-gray-400 hover:text-purple-500 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                                        title="Preview file"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                    )}

                                    {/* Demo/Org Badge */}
                                    {file.is_shared && (
                                      <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex-shrink-0">
                                        {file.shared_by === 'owner' ? (
                                          <Globe className="w-3 h-3" />
                                        ) : (
                                          <Users className="w-3 h-3" />
                                        )}
                                        {file.shared_by === 'owner' ? 'Demo' : 'Org'}
                                      </span>
                                    )}

                                    {/* Share Button (owner can share as demo, admin can share as org) */}
                                    {file.is_own && (user?.role === 'owner' || user?.role === 'admin') && (
                                      <button
                                        onClick={(e) => handleShare(file.id, file.is_shared ? (file.shared_by === 'owner' ? 'demo' : 'org') : null, e)}
                                        className={`p-1.5 rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0 ${
                                          file.is_shared
                                            ? 'text-blue-500 hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                            : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                        }`}
                                        title={file.is_shared ? 'Make private' : (user?.role === 'owner' ? 'Share with all users' : 'Share with organization')}
                                      >
                                        {file.is_shared ? <Globe className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                                      </button>
                                    )}

                                    {/* Delete Button (only for own files, hidden in edit mode) */}
                                    {file.is_own && !editMode && (
                                      <button
                                        onClick={(e) => handleDelete(file.id, e)}
                                        className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                                        title="Delete file"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Storage Usage */}
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Storage</span>
                    <span>
                      {(Number(usage.total_size_mb) || 0).toFixed(1)}/{limits.max_total_storage_mb || 5} MB
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-600 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, ((Number(usage.total_size_mb) || 0) / (limits.max_total_storage_mb || 5)) * 100)}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Context Summary */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Query Context
          </div>
          <div className="flex flex-wrap gap-2">
            {activeProject && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                <FolderOpen className="w-3 h-3" />
                {activeProject.name}
              </span>
            )}
            {dbIncluded && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">
                <Database className="w-3 h-3" />
                {activeDatasource.name}
              </span>
            )}
            {fileCount > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                <FileText className="w-3 h-3" />
                {fileCount} file{fileCount !== 1 ? 's' : ''}
              </span>
            )}
            {!activeProject && !dbIncluded && fileCount === 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                No data sources selected
              </span>
            )}
          </div>
        </div>
          </>
        )}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        file={previewingFile}
        isOpen={!!previewingFile}
        onClose={() => setPreviewingFile(null)}
      />

      {/* Bulk Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => !bulkDeleting && setShowDeleteConfirm(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 z-50 w-80">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Delete Files
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {editSelectedIds.size} file{editSelectedIds.size !== 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete {editSelectedIds.size === 1 ? 'this file' : 'these files'}? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={bulkDeleting}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {bulkDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
